import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react'
import {useParams} from 'react-router-dom/cjs/react-router-dom.min'
import {createOrUpdateGenericInfo} from '../../api/ServiceWorker'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import useWindowSize from '../../utils/useWindowSize'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {component, formLayout} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {permissionOptionKeys, roleList} from './RoleConstant'

const PATH = 'role'
const QueryKey = 'Role'

const RoleManagementForm = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    const {id: idString} = useParams()
    const id = useMemo(() => {
        return idString ? parseInt(idString) : null
    }, [idString])

    const queryClient = useQueryClient()
    const {user} = useAuth()
    const {translateCommons} = TranslateUtils()
    const [visible, setVisible] = useState(false)
    const handleShowForm = useCallback(() => {
        blockOverflownScroll()
        setVisible(true)
    }, [])

    const handleHideForm = useCallback(() => {
        unblockOverflownScroll()
        setVisible(false)
    }, [])

    const {mutate: handleUpdateRole} = useMutation(
        async toPostData =>
            await createOrUpdateGenericInfo(PATH, user?.id, toPostData),
        {
            onSuccess: data => {
                if (data?.id) {
                    showNotification(
                        severityNotification.success,
                        'Role Created !!',
                    )

                    queryClient.setQueryData(
                        [QueryKey, user?.id],
                        oldDataList => {
                            return oldDataList && Array.isArray(oldDataList)
                                ? [data, ...oldDataList]
                                : [data]
                        },
                    )

                    handleHideForm()
                }
            },
        },
    )

    const handleOnSubmit = async value => {
        if (!value?.name) {
            showNotification(severityNotification.error, 'Please input name')
            return
        }

        if (!value?.permissions || value?.permissions?.length === 0) {
            showNotification(
                severityNotification.error,
                'Please select at least one permission',
            )
            return
        }

        let toPostData = {
            ...value,
            clientId: id,
        }

        handleUpdateRole(toPostData)
    }

    const dataList = useMemo(() => {
        let tmp = [
            {
                key: permissionOptionKeys.basicInformation,
                description: '',
                children: [
                    {
                        key: permissionOptionKeys.name,
                        component: component.inputText,
                        className: 'col-12',
                        placeholder: 'Input here ( For displaying )',
                        layout: 'flex',
                    },
                    {
                        key: permissionOptionKeys.description,
                        component: component.inputTextArea,
                        placeholder: 'Input here ( For management )',
                        className: 'col-12',
                        layout: 'flex',
                    },
                ],
            },
            {
                key: permissionOptionKeys.selectAllOrOnly,
                description: '',
                children: [
                    {
                        key: permissionOptionKeys.selectAll,
                        component: component.dropdown,
                        options: [
                            {label: 'All permissions', value: true},
                            {label: 'Only some permissions', value: false},
                        ],
                        className: 'col-12',
                        placeholder: 'Selected all role or only',
                        layout: 'flex',
                        defaultValue: false,
                    },
                ],
            },
        ]

        return tmp.concat(roleList)
    }, [])
    const width = useWindowSize()
    return (
        <React.Fragment>
            <Sidebar
                fullScreen={width <= 1024 ? true : false}
                className={`custom p-sidebar-lg`}
                position="right"
                style={{width: width <= 1024 ? '80rem' : false}}
                visible={visible}
                onHide={() => handleHideForm()}
                blockScroll>
                <PageContentWrapper>
                    <FormHeader
                        header={translateCommons('ROLE FORM')}
                        onHide={() => handleHideForm()}
                    />
                    <CustomForm
                        dataList={dataList}
                        layout={formLayout.group}
                        onSubmit={handleOnSubmit}
                        isRoleData={true}
                    />
                </PageContentWrapper>
            </Sidebar>
        </React.Fragment>
    )
})

export default RoleManagementForm
